import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Snackbar,
  Stack,
} from '@mui/material';
import { useProjectContext } from '../context/ProjectContext';
import { prettyPrint } from '../utils/dataUtils';
import {
  DraftHeader,
  DraftMetadataCard,
  DraftDefinitionCard,
  PromotionControlsCard,
  DiffValidationCard,
  ApprovalExportsCard,
  ReviewCommentsCard,
  EnrichmentCard,
  EnrichmentLedgerCard,
  RejectDraftCard,
  useDraftWorkspaceData,
  useDraftMutations,
  useEnrichmentForm,
} from './schema-draft-workspace';
import type { ToastState } from './schema-draft-workspace';

const SchemaDraftWorkspace: React.FC = () => {
  const { schemaId } = useParams<{ schemaId: string }>();
  const navigate = useNavigate();
  const { selectedProjectId, targetSchema, refreshSummary } = useProjectContext();

  const [promoteSchemaId, setPromoteSchemaId] = useState<string>(targetSchema?.schema_id ?? '');
  const [promoteSchemaName, setPromoteSchemaName] = useState<string>('');
  const [promoteEnvironment, setPromoteEnvironment] = useState<string>('default');
  const [diffSchemaId, setDiffSchemaId] = useState<string>(targetSchema?.schema_id ?? '');
  const [rejectReason, setRejectReason] = useState<string>('');
  const [toast, setToast] = useState<ToastState | null>(null);
  const [commentDraft, setCommentDraft] = useState<string>('');
  const [commentSubmitting, setCommentSubmitting] = useState<boolean>(false);

  const workspaceData = useDraftWorkspaceData({
    schemaId,
    diffSchemaId,
    promoteSchemaId,
    setPromoteSchemaId,
    setDiffSchemaId,
    setPromoteSchemaName,
    promoteSchemaIdCurrent: promoteSchemaId,
    diffSchemaIdCurrent: diffSchemaId,
    promoteSchemaNameCurrent: promoteSchemaName,
    schemaFromContext: targetSchema ?? null,
  });

  const {
    draftQuery,
    schemaSetQuery,
    ledgerQuery,
    draft,
    ledgerWorkbookId,
    schemaSummary,
    enrichmentMetadata,
    tableEnrichmentMap,
    fieldEnrichmentMap,
    schemaEnrichmentSnapshot,
    tableSnapshotMap,
    fieldSnapshotMap,
    schemaEnrichmentVersion,
    reviewComments,
    schemaAdjustments,
    sortedSchemaAdjustments,
    tablesDefinition,
    tableNames,
    schemaSetTableNames,
    selectedSchemaSetName,
    selectedSchemaSetStatus,
  } = workspaceData;

  const mutations = useDraftMutations({
    schemaId,
    draft,
    rejectReason,
    promoteSchemaId,
    promoteSchemaName,
    promoteEnvironment,
    enrichmentMetadata,
    schemaEnrichmentSnapshot,
    schemaEnrichmentVersion,
    commentDraft,
    setCommentDraft,
    setCommentSubmitting,
    setToast,
    refreshSummary,
  });

  const enrichmentForm = useEnrichmentForm({
    draft,
    tablesDefinition,
    tableNames,
    tableEnrichmentMap,
    fieldEnrichmentMap,
    tableSnapshotMap,
    fieldSnapshotMap,
    sortedSchemaAdjustments,
    ledgerWorkbookId,
    updateDraftMetadataMutation: mutations.updateDraftMetadataMutation,
    setToast,
  });

  if (draftQuery.isLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (draftQuery.error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">
          Unable to load schema draft.{' '}
          {draftQuery.error instanceof Error ? draftQuery.error.message : 'Please try again later.'}
        </Alert>
        <Button sx={{ mt: 2 }} variant="outlined" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Box>
    );
  }

  if (!draft) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">Schema draft not found.</Alert>
        <Button sx={{ mt: 2 }} variant="outlined" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Box>
    );
  }

  const currentStatus = draft.status;
  const isGenerated = currentStatus === 'generated';
  const isApproved = currentStatus === 'approved';

  return (
    <Box sx={{ p: 3 }}>
      <DraftHeader
        status={currentStatus}
        isApproved={isApproved}
        isGenerated={isGenerated}
        onBack={() => navigate(-1)}
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Stack spacing={3}>
            <DraftMetadataCard
              draft={draft}
              selectedProjectId={selectedProjectId}
              suggestedName={schemaSummary?.suggestedName ?? ''}
              tableCount={schemaSummary?.tableCount ?? 0}
            />
            <DraftDefinitionCard
              draft={draft}
              tablesDefinition={tablesDefinition}
              enrichmentMetadata={enrichmentMetadata}
            />
            {draft.metadata && Object.keys(draft.metadata).length > 0 && (
              <Paper variant="outlined" sx={{ p: 2, maxHeight: 320, overflow: 'auto' }}>
                <pre style={{ margin: 0, fontSize: 12 }}>{prettyPrint(draft.metadata)}</pre>
              </Paper>
            )}
          </Stack>
        </Grid>

        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            <PromotionControlsCard
              promoteSchemaId={promoteSchemaId}
              setPromoteSchemaId={setPromoteSchemaId}
              promoteSchemaName={promoteSchemaName}
              setPromoteSchemaName={setPromoteSchemaName}
              promoteEnvironment={promoteEnvironment}
              setPromoteEnvironment={setPromoteEnvironment}
              isPromoting={mutations.promoteMutation.isLoading}
              isApproved={isApproved}
              onPromote={mutations.handlePromoteDraft}
              setToast={setToast}
            />

            <DiffValidationCard
              schemaId={draft.schema_id}
              diffSchemaId={diffSchemaId}
              setDiffSchemaId={setDiffSchemaId}
              schemaSetTableNames={schemaSetTableNames}
              selectedSchemaSetName={selectedSchemaSetName}
              selectedSchemaSetStatus={selectedSchemaSetStatus}
              isSchemaLoading={schemaSetQuery.isLoading}
              isSchemaError={schemaSetQuery.isError}
              setToast={setToast}
            />

            <ApprovalExportsCard
              draft={draft}
              schemaSummary={schemaSummary as Record<string, unknown> | null}
              enrichmentMetadata={enrichmentMetadata}
              schemaEnrichmentSnapshot={schemaEnrichmentSnapshot}
              reviewComments={reviewComments}
            />

            <ReviewCommentsCard
              reviewComments={reviewComments}
              commentDraft={commentDraft}
              setCommentDraft={setCommentDraft}
              commentSubmitting={commentSubmitting}
              onAddComment={mutations.handleAddComment}
            />

            <EnrichmentCard
              schemaEnrichmentVersion={schemaEnrichmentVersion}
              tableNames={tableNames}
              isMetadataMutationLoading={mutations.updateDraftMetadataMutation.isLoading}
              selectedTable={enrichmentForm.selectedTable}
              setSelectedTable={enrichmentForm.setSelectedTable}
              tableDescription={enrichmentForm.tableDescription}
              setTableDescription={enrichmentForm.setTableDescription}
              tableOwners={enrichmentForm.tableOwners}
              setTableOwners={enrichmentForm.setTableOwners}
              tableTags={enrichmentForm.tableTags}
              setTableTags={enrichmentForm.setTableTags}
              tableClassification={enrichmentForm.tableClassification}
              setTableClassification={enrichmentForm.setTableClassification}
              tableSensitivity={enrichmentForm.tableSensitivity}
              setTableSensitivity={enrichmentForm.setTableSensitivity}
              tableLastUpdatedAt={enrichmentForm.tableLastUpdatedAt}
              tableLastUpdatedBy={enrichmentForm.tableLastUpdatedBy}
              onSaveTableEnrichment={enrichmentForm.handleSaveTableEnrichment}
              selectedFieldTable={enrichmentForm.selectedFieldTable}
              setSelectedFieldTable={enrichmentForm.setSelectedFieldTable}
              selectedFieldName={enrichmentForm.selectedFieldName}
              setSelectedFieldName={enrichmentForm.setSelectedFieldName}
              selectedTableColumns={enrichmentForm.selectedTableColumns}
              fieldDescription={enrichmentForm.fieldDescription}
              setFieldDescription={enrichmentForm.setFieldDescription}
              fieldOwners={enrichmentForm.fieldOwners}
              setFieldOwners={enrichmentForm.setFieldOwners}
              fieldTags={enrichmentForm.fieldTags}
              setFieldTags={enrichmentForm.setFieldTags}
              fieldClassification={enrichmentForm.fieldClassification}
              setFieldClassification={enrichmentForm.setFieldClassification}
              fieldSensitivity={enrichmentForm.fieldSensitivity}
              setFieldSensitivity={enrichmentForm.setFieldSensitivity}
              fieldLastUpdatedAt={enrichmentForm.fieldLastUpdatedAt}
              fieldLastUpdatedBy={enrichmentForm.fieldLastUpdatedBy}
              onSaveFieldEnrichment={enrichmentForm.handleSaveFieldEnrichment}
              enrichmentSaving={enrichmentForm.enrichmentSaving}
            />

            <EnrichmentLedgerCard
              ledgerWorkbookId={ledgerWorkbookId}
              isLedgerLoading={ledgerQuery.isLoading}
              isLedgerError={ledgerQuery.isError}
              schemaAdjustments={schemaAdjustments}
            />

            {!isApproved && (
              <RejectDraftCard
                rejectReason={rejectReason}
                setRejectReason={setRejectReason}
                isUpdating={mutations.updateDraftStatusMutation.isLoading}
                onReject={mutations.handleRejectDraft}
              />
            )}
          </Stack>
        </Grid>
      </Grid>

      {toast && (
        <Snackbar
          open
          autoHideDuration={5000}
          onClose={() => setToast(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={() => setToast(null)} severity={toast.severity} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};

export default SchemaDraftWorkspace;
